import {
  AccidentIssueAnyoneInjuredEnum,
  AccidentIssueDocument,
  AccidentIssueMedicalNeededEnum,
  AccidentIssueSafetyNotifiedEnum,
  AccidentIssueWasCeoCalledEnum,
  AccidentIssueWasCrewInvolvedEnum,
  AccidentIssueWithCache,
  AccidentIssueWithCacheCanVehicleBeDrivenEnum,
  AccidentIssueWithCacheDidDriverCallHelpEnum,
  AccidentIssueWithCacheSamsaraCameraWorkingEnum,
  AccidentIssueWithCacheWasDriverAtFaultEnum,
  AccidentIssueWithCacheWasThirdPartyInvolvedEnum,
  Trip,
} from '@/models/gen/graphql';
import { Datetime, Schema } from '@/utils';
import { clearFormStorage, getFormStorage, setFormStorage } from '@/common/Form';

export namespace AccidentHistoryFormDefinitions {
  export type Locale =
    | 'SUBMIT_BUTTON_TEXT'
    | 'SUBMIT_BUTTON_COLOR'
    | 'Accident'
    | 'Description of Accident'
    | 'Station'
    | 'Accident Date/Time'
    | 'Driver Name'
    | 'Van Number'
    | 'Dispatcher Name'
    | 'Samsara Camera Working?'
    | 'Specific Location Of The Accident'
    | 'Was 3rd Party Involved?'
    | '3rd Party Information'
    | 'Describe Damage To Other Vehicles'
    | 'Anyone Injured?'
    | 'Medical Attention Needed?'
    | 'Outbound FLT #'
    | 'Outbound Date/Time'
    | 'What Type Of Medical Attention Was Offered/Required?'
    | 'Treated on scene, which hospital were they sent to, etc'
    | 'Information Of All Injured Parties'
    | 'Were Crew Onboard?'
    | 'Information Of All Crew Members'
    | 'Name and phone numbers'
    | 'Did Driver Call 911?'
    | 'Can Vehicle Be Driven?'
    | 'Was Driver At Fault?'
    | 'Was CEO Called?'
    | 'Check All That Apply:'
    | 'Arranged another driver to transport crew members (if needed)'
    | 'Driver contacted manager'
    | 'You contacted manager'
    | 'Was Safety Notified?'
    | 'Who Is Safety On Call?'
    | 'Create Accident'
    | 'Accident History'
    | 'Required'
    | 'Must not exceed 512 characters'
    | 'Must not exceed 256 characters'
    | 'Must not exceed 64 characters'
    | 'Must be exactly 4 characters'
    | 'RECOVERY INFORMATION';
  export type Value = Partial<AccidentIssueWithDocuments>;
  export type Props = {
    onSubmit?: (data: Value) => void | Value | Promise<Value> | undefined;
    onEditAccident?: (data: Value) => void | Value | Promise<Value> | undefined;
    locale?: Partial<Record<Locale, string>>;
    value?: Value;
    trip?: Partial<Trip>;
  };
  export const initValue: Value = {
    accidentDatetime: '',
    additionalDriverScheduled: undefined,
    anyoneInjured: undefined,
    canVehicleBeDriven: undefined,
    damageToOtherVehicles: '',
    description: '',
    didDriverCallHelp: undefined,
    dispatcherContactedManager: undefined,
    dispatcherId: '',
    driverContactedManager: undefined,
    driverId: '',
    infoOfCrewMembers: '',
    infoOfInjuredParties: '',
    medicalNeeded: undefined,
    safetyId: '',
    safetyNotified: undefined,
    specificLocation: '',
    stationText: '',
    thirdPartyInformation: '',
    traceId: '',
    tripId: '',
    typeOfMedicalAttention: '',
    vehicleId: '',
    wasCeoCalled: undefined,
    wasCrewInvolved: undefined,
    wasDriverAtFault: undefined,
    wasThirdPartyInvolved: undefined,
    outboundDatetime: undefined,
    outboundFlightName: undefined,
    documents: [],
  };
  export const getLocale = (value: Value): Partial<Record<Locale, string>> => ({
    SUBMIT_BUTTON_TEXT: value.id ? 'Save Accident' : 'Create Accident',
    SUBMIT_BUTTON_COLOR: value.id ? 'primary' : 'success',
  });
  export const validator = (locale: (original: Locale) => string) =>
    Schema((z) =>
      z.object({
        stationText: z.string().min(1, { message: locale('Required') }),
        accidentDatetime: z.string().refine((val: string): boolean => !!val && !!new Datetime(val).toString()),
        driverId: z.string().uuid({ message: 'Required' }),
        vehicleId: z.string().uuid({ message: 'Required' }),
        dispatcherId: z.string().uuid({ message: 'Required' }),
        samsaraCameraWorking: z.nativeEnum(AccidentIssueWithCacheSamsaraCameraWorkingEnum, { message: locale('Required') }),
        specificLocation: z
          .string()
          .min(1, { message: locale('Required') })
          .max(256, { message: locale('Must not exceed 256 characters') }),
        wasThirdPartyInvolved: z.nativeEnum(AccidentIssueWithCacheWasThirdPartyInvolvedEnum, { message: locale('Required') }),
        thirdPartyInformation: z.string().min(1, { message: locale('Required') }),
        damageToOtherVehicles: z.string().min(1, { message: locale('Required') }),
        anyoneInjured: z.nativeEnum(AccidentIssueAnyoneInjuredEnum, { message: locale('Required') }),
        medicalNeeded: z.nativeEnum(AccidentIssueMedicalNeededEnum, { message: locale('Required') }),
        typeOfMedicalAttention: z.string().min(1, { message: locale('Required') }),
        infoOfInjuredParties: z.string().min(1, { message: locale('Required') }),
        wasCrewInvolved: z.nativeEnum(AccidentIssueWasCrewInvolvedEnum, { message: locale('Required') }),
        infoOfCrewMembers: z.string().min(1, { message: locale('Required') }),
        didDriverCallHelp: z.nativeEnum(AccidentIssueWithCacheDidDriverCallHelpEnum, { message: locale('Required') }),
        canVehicleBeDriven: z.nativeEnum(AccidentIssueWithCacheCanVehicleBeDrivenEnum, { message: locale('Required') }),
        wasDriverAtFault: z.nativeEnum(AccidentIssueWithCacheWasDriverAtFaultEnum, { message: locale('Required') }),
        wasCeoCalled: z.nativeEnum(AccidentIssueWasCeoCalledEnum, { message: locale('Required') }),
        safetyId: z.string().uuid({ message: 'Required' }),
        safetyNotified: z.nativeEnum(AccidentIssueSafetyNotifiedEnum, { message: locale('Required') }),
        description: z.string().min(1, { message: locale('Required') }),
        outboundDatetime: z.string().refine((val: string): boolean => !!val && !!new Datetime(val).toString()),
        outboundFlightName: z.string().max(64, { message: locale('Must not exceed 64 characters') }),
        tripId: z.string().uuid({ message: 'Required' }),
      })
    );
  export const searchable = {
    stationText: (val) => val,
    accidentDatetime: (val) => val,
    tripServicerIataAirlineCode: (val) => val,
    tripFlightNumber: (val) => val,
    tripScheduled: (val) => val,
    vehicleTrackingId: (val) => val,
    driverFirstName: (val, full) => [val, full?.driverLastName || ''].join(' ').trim(),
    driverEmployeeId: (val) => val,
    safetyFirstName: (val, full) => [val, full?.safetyLastName || ''].join(' ').trim(),
    anyoneInjured: (val) => val,
    wasCrewInvolved: (val) => val,
  };
  export const saveForm = (values: Value): void => setFormStorage('AccidentHistoryForm', values);
  export const loadForm = (): Value => getFormStorage('AccidentHistoryForm');
  export const clearForm = (): void => clearFormStorage('AccidentHistoryForm');
}
export type AccidentIssueWithDocuments = AccidentIssueWithCache & {
  documents: AccidentIssueDocument[];
};
